/**
 * Web Bluetooth heart-rate monitor hook.
 *
 * Connects to any BLE device that advertises the standard GATT
 * `heart_rate` service (0x180D) — Polar, Wahoo TICKR, Garmin HRM-Pro,
 * COOSPO, Scosche, most modern smartwatches in "broadcast HR" mode.
 *
 * Parses the Heart Rate Measurement characteristic (0x2A37) which is
 * a bit-packed struct — first byte is a flags byte telling us if the
 * BPM value is 8-bit or 16-bit and whether RR-interval + energy data
 * follows.
 *
 * Calories are estimated in real time using the Keytel-2005 formula
 * (avg of male + female to stay gender-neutral) using the user's stored
 * weight (lbs → kg) and age. Session samples are averaged for the final
 * summary emitted on disconnect.
 */
import { useCallback, useEffect, useRef, useState } from 'react';

const HR_SERVICE_UUID = 'heart_rate';           // 0x180D
const HR_MEASUREMENT_UUID = 'heart_rate_measurement'; // 0x2A37

// Keytel-2005 combined (kJ/min from HR + weight_kg + age). Gender-neutral
// average of the male and female formulas.
const keytelKcalPerMinute = (hr, weightKg, age) => {
  if (!hr || !weightKg || !age) return 0;
  const male = -55.0969 + 0.6309 * hr + 0.1988 * weightKg + 0.2017 * age;
  const female = -20.4022 + 0.4472 * hr - 0.1263 * weightKg + 0.0740 * age;
  const kJ = Math.max(0, (male + female) / 2);
  return kJ / 4.184; // kJ → kcal
};

const parseHeartRate = (dataView) => {
  // Byte 0 = flags. Bit 0: 0 = uint8 HR, 1 = uint16 HR.
  const flags = dataView.getUint8(0);
  const is16Bit = flags & 0x01;
  const bpm = is16Bit ? dataView.getUint16(1, true) : dataView.getUint8(1);
  return bpm;
};

export const useHeartRateMonitor = ({ weightLbs, age } = {}) => {
  const [supported, setSupported] = useState(false);
  const [status, setStatus] = useState('idle'); // idle | requesting | connecting | streaming | error
  const [error, setError] = useState(null);
  const [deviceName, setDeviceName] = useState('');
  const [bpm, setBpm] = useState(null);
  const [avgBpm, setAvgBpm] = useState(0);
  const [maxBpm, setMaxBpm] = useState(0);
  const [calories, setCalories] = useState(0);
  const [durationSec, setDurationSec] = useState(0);

  const deviceRef = useRef(null);
  const charRef = useRef(null);
  const bpmSamplesRef = useRef([]);
  const startedAtRef = useRef(null);
  const lastTickRef = useRef(null);
  const tickIdRef = useRef(null);

  // Feature detection — Web Bluetooth needs `navigator.bluetooth`.
  useEffect(() => {
    setSupported(typeof navigator !== 'undefined' && !!navigator.bluetooth);
  }, []);

  const weightKg = weightLbs ? weightLbs * 0.453592 : null;

  const handleMeasurement = useCallback((event) => {
    const hr = parseHeartRate(event.target.value);
    if (!hr || hr < 30 || hr > 240) return; // sanity
    setBpm(hr);
    bpmSamplesRef.current.push(hr);
    if (hr > maxBpm) setMaxBpm(hr);
    const sum = bpmSamplesRef.current.reduce((a, b) => a + b, 0);
    setAvgBpm(Math.round(sum / bpmSamplesRef.current.length));
  }, [maxBpm]);

  // Ticker updates duration + integrates calories per second using the
  // most recent BPM sample. Independent of the BLE notification rate.
  useEffect(() => {
    if (status !== 'streaming') return undefined;
    tickIdRef.current = setInterval(() => {
      const now = Date.now();
      const startedAt = startedAtRef.current || now;
      setDurationSec(Math.floor((now - startedAt) / 1000));

      if (bpm && weightKg && age && lastTickRef.current) {
        const dtSec = (now - lastTickRef.current) / 1000;
        const kcalPerSec = keytelKcalPerMinute(bpm, weightKg, age) / 60;
        setCalories((prev) => Math.max(0, prev + kcalPerSec * dtSec));
      }
      lastTickRef.current = now;
    }, 1000);
    return () => clearInterval(tickIdRef.current);
  }, [status, bpm, weightKg, age]);

  const connect = useCallback(async () => {
    if (!navigator.bluetooth) {
      setError('Web Bluetooth is not supported in this browser.');
      setStatus('error');
      return;
    }
    setStatus('requesting');
    setError(null);
    try {
      const device = await navigator.bluetooth.requestDevice({
        filters: [{ services: [HR_SERVICE_UUID] }],
        optionalServices: [HR_SERVICE_UUID],
      });
      deviceRef.current = device;
      setDeviceName(device.name || 'Heart Rate Monitor');
      device.addEventListener('gattserverdisconnected', () => {
        setStatus('idle');
        setBpm(null);
      });

      setStatus('connecting');
      const server = await device.gatt.connect();
      const service = await server.getPrimaryService(HR_SERVICE_UUID);
      const char = await service.getCharacteristic(HR_MEASUREMENT_UUID);
      charRef.current = char;

      char.addEventListener('characteristicvaluechanged', handleMeasurement);
      await char.startNotifications();

      bpmSamplesRef.current = [];
      startedAtRef.current = Date.now();
      lastTickRef.current = Date.now();
      setBpm(null);
      setAvgBpm(0);
      setMaxBpm(0);
      setCalories(0);
      setDurationSec(0);
      setStatus('streaming');
    } catch (e) {
      // User cancelled the picker or the device rejected the connection.
      if (e && e.name === 'NotFoundError') {
        setStatus('idle');
        return;
      }
      setError(e?.message || 'Failed to connect');
      setStatus('error');
    }
  }, [handleMeasurement]);

  const disconnect = useCallback(() => {
    try {
      if (charRef.current) {
        charRef.current.removeEventListener('characteristicvaluechanged', handleMeasurement);
        try { charRef.current.stopNotifications(); } catch { /* noop */ }
      }
      if (deviceRef.current?.gatt?.connected) {
        deviceRef.current.gatt.disconnect();
      }
    } catch { /* noop */ }
    setStatus('idle');
  }, [handleMeasurement]);

  // Cleanup on unmount
  useEffect(() => () => disconnect(), [disconnect]);

  return {
    supported,
    status,
    error,
    deviceName,
    bpm,
    avgBpm,
    maxBpm,
    calories: Math.round(calories),
    durationSec,
    connect,
    disconnect,
  };
};

export default useHeartRateMonitor;
